import { useRouter } from 'next/router';
import qs from 'qs';
import React, { useEffect } from 'react';
import SeoHead from '@/components/head/SeoHead';
import { CHAIN_ID } from '@/constants/constants';
import { loadDealProposal } from '@/utilities/deals/loadDealProposal';
import { getAlchemyNetwork, truncateAddress } from '@/utilities/functions';
import { getNftThumbnail } from '@/utilities/getNftThumbnail';
import { removeExtraSpaces } from '@/utilities/removeExtraSpaces';
import type { GetServerSideProps, NextPage } from 'next';

type Props = {
  ogImage: string;
  ogTitle: string;
  ogDescription: string;
};

const DealBidPage: NextPage<Props> = ({
  ogImage,
  ogTitle,
  ogDescription,
}: Props) => {
  const router = useRouter();

  useEffect(() => {
    router.push(`/deal/${router.query.id}`);
  }, [router]);

  return (
    <SeoHead title={ogTitle} imageURI={ogImage} description={ogDescription} />
  );
};
export default DealBidPage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const dealId = Number(context.params?.id);
  const bidId = Number(context.params?.bidId);
  const { dealBid } = await loadDealProposal({ dealId, bidId });

  if (!dealBid || dealBid.Bids.length === 0) {
    return {
      notFound: true,
    };
  }
  const { nft_contract_address, token_id, Deal, Collection, User } =
    dealBid.Bids[0];

  const network = getAlchemyNetwork(CHAIN_ID);
  const nftImage = await getNftThumbnail({
    network: network,
    nftContractAddress: nft_contract_address,
    tokenId: token_id,
  });
  let ogTitle = `Proposal Submitted: ${Collection.name} #${token_id} for ${Deal.Brand.company_name}`;

  const userName =
    User.username === null
      ? truncateAddress(User.wallet_address)
      : User.username;

  let ogDescription = `${userName} submitted a proposal to collaborate with ${Deal.Brand.company_name} on ${Deal.title}. Explore the details of the opportunity now!`;

  ogDescription = removeExtraSpaces(ogDescription);
  ogTitle = removeExtraSpaces(ogTitle);

  const params: { [key: string]: string | null | undefined } = {
    nftImage,
    dealName: Deal.title,
    collection: Collection.name,
    tokenId: token_id.toString(),
  };

  // Remove any empty parameters
  Object.keys(params).forEach((key) => {
    if (!params[key]) {
      delete params[key];
    }
  });

  const queryString = qs.stringify(params);

  const host =
    process.env.NEXTAUTH_URL ||
    (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : null);

  const ogImage = `${host}/api/og/proposal?${queryString}`;

  return {
    props: { ogImage, ogTitle, ogDescription },
  };
};
